<template>
  <!-- 状态栏统一打开日志抽屉，最新日志通过事件交给状态栏展示。 -->
  <UiDrawer
    v-model="drawerVisible"
    direction="btt"
    size="calc(100% - var(--ui-terminal-header-height))"
    custom-class="realtime-log-drawer"
    wrapper-class="realtime-log-drawer-layer"
  >
    <template #title>
      <div class="realtime-log-drawer__title">
        <div class="realtime-log-drawer__heading">
          <i class="el-icon-document"></i>
          <strong>{{ $t("实时日志") }}</strong>
          <span>{{ $t("{0} 条", [(visibleLogCount)]) }}</span>
        </div>
        <UiButton tone="danger" size="small" icon="el-icon-delete" @click.stop="clearLogs">{{ $t("清空日志") }}</UiButton>
      </div>
    </template>

    <div class="ui-drawer__toolbar realtime-log-toolbar">
      <UiColorFilter v-model="checkedLogTypes" class="realtime-log-types" :options="logTypeOptions" :aria-label="$t('日志类型筛选')" />
      <UiMultiSelect
        v-if="planeOptions.length"
        v-model="selectedPlanes"
        class="realtime-log-plane-select"
        :placeholder="$t('全部飞机')"
        :options="planeOptions"
        all-option
        :all-value="allPlanesValue"
        :all-label="$t('全部飞机')"
        popper-class="realtime-log-select-dropdown"
      />
    </div>

    <div class="realtime-log-list">
      <div v-if="!displayLogs.length || visibleLogCount === 0" class="realtime-log-empty">{{ $t("暂无实时日志") }}</div>
      <UiTimeline v-if="displayLogs.length" compact class="realtime-log-timeline">
        <el-timeline-item
          v-for="item in displayLogs"
          :key="item.key"
          :color="item.color"
          :timestamp="formatTimeRange(item)"
          placement="top"
        >
          <div class="realtime-log-item">
            <UiTag v-if="item.planeName" tone="neutral" class="realtime-log-plane" :title="item.planeName"><span class="ui-tag__text">{{ item.planeName }}</span></UiTag>
            <span class="realtime-log-content">{{ item.content }}</span>
          </div>
        </el-timeline-item>
      </UiTimeline>
    </div>
  </UiDrawer>
</template>

<script>
import { parseTime } from '@/utils'
import mqtt from '@/utils/mqtt'
import { getStorageJson } from '@/utils/storage'
import { getEventThemeColor } from '@/utils/theme'

const SOURCE_TYPES = [
  { value: 'fc', label: '飞控' },
  { value: 'esp', label: '机载端' },
  { value: 'terminal', label: '终端操作' },
  { value: 'system', label: '系统' }
]
const ALL_PLANES_VALUE = '__all_planes__'
const LOG_PREVIEW_DURATION = 8000

export default {
  name: 'GroundStationRealtimeLog',
  props: {
    value: { type: Boolean, default: false }
  },
  data () {
    return {
      selectedPlanes: [ALL_PLANES_VALUE],
      checkedLogTypes: SOURCE_TYPES.map(item => item.value),
      previewVisible: false,
      previewTimer: null
    }
  },
  computed: {
    drawerVisible: {
      get () { return this.value },
      set (value) { this.$emit('input', value) }
    },
    logs () { return this.$store.state.logList || [] },
    logTypeOptions () {
      return SOURCE_TYPES.map(item => ({ ...item, color: getEventThemeColor(item.value) }))
    },
    groupedLogs () {
      return this.logs.reduce((list, rawItem, index) => {
        const sourceType = this.normalizeSourceType(rawItem.sourceType || rawItem.source_type)
        const item = { ...rawItem, sourceType, color: getEventThemeColor(sourceType) }
        const previous = list[list.length - 1]
        const groupKey = `${item.sourceType}_${item.planeName || ''}_${item.content || ''}`
        if (previous && previous.groupKey === groupKey) {
          previous.repeatCount += 1
          previous.endTimestamp = item.timestamp
          previous.key = `${previous.startTimestamp}_${previous.repeatCount}_${index}`
          return list
        }
        list.push({
          ...item,
          groupKey,
          repeatCount: 1,
          startTimestamp: item.timestamp,
          endTimestamp: item.timestamp,
          key: `${item.timestamp}_${index}`
        })
        return list
      }, [])
    },
    previewLog () {
      return this.previewVisible
        ? (this.groupedLogs[this.groupedLogs.length - 1] || null)
        : null
    },
    planeOptions () {
      return [...new Set(this.logs.map(item => item.planeName).filter(Boolean))]
    },
    displayLogs () {
      return this.groupedLogs.filter(item => this.isLogVisible(item)).reverse()
    },
    visibleLogCount () {
      return this.displayLogs.length
    },
    allPlanesValue () { return ALL_PLANES_VALUE },
    newestLog () { return this.logs[this.logs.length - 1] || null },
    terminalLogTopic () {
      const userInfo = getStorageJson('userInfo', {})
      const topicPrefix = this.$store.state.user.topic_prefix || userInfo.topic_prefix
      return topicPrefix || ''
    },
    operatorAccount () {
      const userInfo = getStorageJson('userInfo', {})
      return this.$store.state.user.name || userInfo.name
    }
  },
  watch: {
    previewLog (log) { this.$emit('preview-change', log) },
    newestLog (logData) {
      this.showLatestLogPreview(logData)
      if (logData && this.normalizeSourceType(logData.sourceType) === 'terminal') this.publishTerminalLog(logData)
    }
  },
  beforeDestroy () {
    this.clearPreviewTimer()
    this.$emit('preview-change', null)
  },
  methods: {
    isLogVisible (item) {
      return this.checkedLogTypes.includes(item.sourceType) &&
        (this.selectedPlanes.includes(ALL_PLANES_VALUE) || this.selectedPlanes.includes(item.planeName))
    },
    normalizeSourceType (sourceType) {
      const value = String(sourceType || '').trim().toLowerCase()
      if (['fc', '飞控'].includes(value)) return 'fc'
      if (['terminal', '终端', '终端操作'].includes(value)) return 'terminal'
      if (['system', '系统'].includes(value)) return 'system'
      return 'esp'
    },
    clearLogs () {
      this.clearPreviewTimer()
      this.previewVisible = false
      this.$store.commit('clearLogList')
    },
    showLatestLogPreview (logData) {
      this.clearPreviewTimer()
      this.previewVisible = Boolean(logData)
      if (!logData) return
      this.previewTimer = window.setTimeout(() => {
        this.previewVisible = false
        this.previewTimer = null
      }, LOG_PREVIEW_DURATION)
    },
    clearPreviewTimer () {
      if (this.previewTimer == null) return
      window.clearTimeout(this.previewTimer)
      this.previewTimer = null
    },
    formatTimeRange (item) {
      const start = parseTime(item.startTimestamp || item.timestamp, '{h}:{i}:{s}')
      const end = parseTime(item.endTimestamp || item.timestamp, '{h}:{i}:{s}')
      const repeat = item.repeatCount > 1 ? ` ×${item.repeatCount}` : ''
      return `${start === end ? start : `${start}–${end}`}${repeat}`
    },
    publishTerminalLog (logData) {
      if (!this.terminalLogTopic || !this.operatorAccount || !logData.planeId || !logData.content) return
      const plane = (this.$store.state.airList || []).find(item => String(item.id) === String(logData.planeId))
      if (!plane || !/^[a-z0-9]{16}$/.test(String(plane.shop_id || ''))) return
      mqtt.publishFun(`terminalLog/${plane.shop_id}/${this.terminalLogTopic}`, JSON.stringify({
        account: this.operatorAccount,
        plane_id: logData.planeId,
        content: logData.content
      }))
    }
  }
}
</script>

<style lang="scss" scoped>
.realtime-log-drawer__title { width: 100%; display: flex; align-items: center; justify-content: space-between; gap: var(--ui-space-lg); padding-right: var(--ui-space-sm); box-sizing: border-box; }
.realtime-log-drawer__heading { min-width: 0; display: flex; align-items: center; gap: var(--ui-space-sm); }
.realtime-log-drawer__heading > i { color: var(--ui-color-primary); font-size: 18px; font-weight: 400; }
.realtime-log-drawer__heading > span { color: var(--ui-text-muted); font-size: var(--ui-font-size-caption); font-weight: 400; }

.ui-drawer__toolbar.realtime-log-toolbar {
  justify-content: flex-start;
  flex-wrap: nowrap;
  gap: var(--ui-space-md);
  overflow-x: auto;
}
.realtime-log-types { flex: 0 0 auto; }
.realtime-log-toolbar .ui-select.realtime-log-plane-select {
  width: 0;
  min-width: 160px;
  max-width: 560px;
  flex: 1 1 0;
}
.realtime-log-list { height: calc(100% - 68px); overflow-y: auto; padding: 0 var(--ui-space-lg) var(--ui-space-lg); box-sizing: border-box; }
.realtime-log-timeline { margin: 0; padding: var(--ui-space-sm) 0 0 var(--ui-space-xs); }
.realtime-log-item { min-height: 28px; display: flex; align-items: center; gap: var(--ui-space-sm); color: var(--ui-text-regular); font-size: var(--ui-font-size-body); }
.realtime-log-plane { max-width: 50%; flex: 0 1 auto; }
.realtime-log-content { min-width: 0; flex: 1; overflow-wrap: anywhere; }
.realtime-log-empty { padding: var(--ui-space-xl); color: var(--ui-text-muted); text-align: center; }

@media (max-width: 720px) {
  .realtime-log-item { flex-wrap: wrap; }
  .realtime-log-plane { max-width: 100%; }
  .realtime-log-content { flex-basis: 100%; padding-bottom: var(--ui-space-xs); }
}
</style>
